import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Card, StatCard } from '@/components/ui/Card';
import { BarList, DimensionTrendChart, RadarChart, TrendChart } from '@/components/ui/Charts';
import { EmptyState, ErrorState, Skeleton } from '@/components/ui/Feedback';
import { Select } from '@/components/ui/Field';
import { Button } from '@/components/ui/Button';
import { PageHeader } from '@/app/PlaceholderPage';
import { cn } from '@/lib/cn';
import { useAuth } from '@/features/auth/AuthContext';
import { databaseApi } from '@/features/database/api';
import { analyticsApi, type IndividualReport } from './api';
import { round1, shortPeriod } from './format';
import { IndividualReportPdfModal } from './IndividualReportPdfModal';

/** L-14 — Rapor kompetensi satu PELITA. */
export function IndividualReportPage() {
  const { user } = useAuth();
  const isPelita = user?.role === 'pelita';
  const [params, setParams] = useSearchParams();
  const [pdfModalOpen, setPdfModalOpen] = useState(false);

  // Jika login sebagai PELITA, kunci ID hanya ke individualId miliknya sendiri.
  const id = isPelita ? (user?.individualId ?? '') : (params.get('pelita') ?? '');

  const list = useQuery({
    queryKey: ['individuals', 'options'],
    queryFn: () => databaseApi.listIndividuals({ page: 1 }),
    enabled: !isPelita,
  });

  const report = useQuery({
    queryKey: ['analytics', 'individual', id],
    queryFn: () => analyticsApi.individualReport(id),
    enabled: Boolean(id),
  });

  const options = list.data?.data ?? [];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={isPelita ? 'Rapor Kompetensi' : 'Rapor Individu'}
        description={
          isPelita
            ? 'Perkembangan kompetensi diri Anda lintas semester.'
            : 'Perkembangan kompetensi seorang PELITA SLI lintas semester.'
        }
        action={
          report.data?.latest && (
            <Button
              className="no-print"
              variant="secondary"
              onClick={() => setPdfModalOpen(true)}
            >
              📄 Cetak / Unduh PDF
            </Button>
          )
        }
      />

      {!isPelita && (
        <Card className="no-print">
          <Select
            label="Pilih PELITA SLI"
            value={id}
            onChange={(e) => setParams(e.target.value ? { pelita: e.target.value } : {})}
          >
            <option value="">— pilih PELITA SLI —</option>
            {options.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} · {p.name}
                {p.tbmName ? ` (${p.tbmName})` : ''}
              </option>
            ))}
          </Select>
        </Card>
      )}

      {report.data?.latest && (
        <IndividualReportPdfModal
          open={pdfModalOpen}
          onClose={() => setPdfModalOpen(false)}
          data={report.data}
        />
      )}

      {!id ? (
        <Card padded={false}>
          <EmptyState
            icon="🧑‍🏫"
            title="Pilih PELITA SLI terlebih dahulu"
            description="Rapor menampilkan penilaian yang sudah dikirim atau diverifikasi."
          />
        </Card>
      ) : report.error ? (
        <ErrorState message={(report.error as Error).message} onRetry={() => report.refetch()} />
      ) : report.isLoading || !report.data ? (
        <Skeleton className="h-72 w-full" />
      ) : !report.data.latest ? (
        <Card padded={false}>
          <EmptyState
            icon="📄"
            title="Belum ada penilaian terkirim"
            description="Belum ada penilaian individu berstatus Terkirim atau Terverifikasi untuk PELITA SLI ini."
          />
        </Card>
      ) : (
        <Body data={report.data} />
      )}
    </div>
  );
}

const INDIVIDUAL_BANDS_DEFINITIONS = [
  { name: 'Pelita Literasi', range: '86 – 100', desc: 'Menginspirasi dan membangun ekosistem literasi melalui kepemimpinan, kolaborasi, dan pemberdayaan sehingga tercipta gerakan literasi yang berdampak dan berkelanjutan.' },
  { name: 'Penggerak Literasi', range: '71 – 85', desc: 'Menggerakkan individu, kelompok, dan berbagai pihak untuk terlibat aktif dalam program literasi serta mendorong tumbuhnya kolaborasi di wilayahnya.' },
  { name: 'Pengamal Literasi', range: '51 – 70', desc: 'Menerapkan pengetahuan dan kompetensi PELITA SLI dalam melaksanakan program, mendampingi masyarakat, dan menjalankan perannya secara konsisten.' },
  { name: 'Pecinta Literasi', range: '26 – 50', desc: 'Berpartisipasi secara aktif dalam kegiatan literasi dan menunjukkan kepedulian terhadap pengembangan TBM serta masyarakat.' },
  { name: 'Pemula', range: '< 26', desc: 'Memahami dasar-dasar literasi serta peran PELITA SLI, dan mulai mengembangkan kompetensi untuk berkontribusi dalam kegiatan literasi.' },
];

function IndividualIdentityCard({ individual }: { individual: IndividualReport['individual'] }) {
  const regionText = individual.regionPath?.map((r) => r.name).join(' → ') || '—';

  return (
    <Card className="border-l-4 border-l-primary bg-primary-soft/10">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        {individual.photoKey ? (
          <img
            src={`/api/v1/files/${individual.photoKey}`}
            alt={individual.name}
            className="size-16 rounded-full object-cover border border-border shadow-xs"
          />
        ) : (
          <div className="size-16 rounded-full bg-primary/10 text-primary font-bold text-xl flex items-center justify-center border border-primary/20 shrink-0">
            {individual.name.charAt(0).toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-primary text-white">
              {individual.code}
            </span>
            <h2 className="text-xl font-bold text-ink">{individual.name}</h2>
            {individual.gender && (
              <span className="text-xs px-2 py-0.5 rounded bg-white border border-border text-ink-secondary">
                {individual.gender === 'L' ? 'Laki-laki' : 'Perempuan'}
              </span>
            )}
          </div>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-1.5 gap-x-6 text-sm text-ink-secondary">
            <div>
              <span className="text-ink-muted">Asal TBM:</span>{' '}
              <strong className="text-ink font-medium">
                {individual.tbmName ? `${individual.tbmCode ? `${individual.tbmCode} · ` : ''}${individual.tbmName}` : '—'}
              </strong>
            </div>
            <div>
              <span className="text-ink-muted">Wilayah:</span>{' '}
              <strong className="text-ink font-medium">{regionText}</strong>
            </div>
            <div>
              <span className="text-ink-muted">Profesi:</span>{' '}
              <strong className="text-ink font-medium">{individual.profession || '—'}</strong>
            </div>
            {individual.personalBranding && (
              <div className="sm:col-span-2 lg:col-span-3 text-xs italic text-ink-secondary">
                "{individual.personalBranding}"
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

function BandDefinitionsCard({
  currentBand,
  bands,
}: {
  currentBand?: string | null;
  bands?: IndividualReport['bands'];
}) {
  const list =
    bands && bands.length > 0
      ? bands.map((b) => ({
          name: b.name,
          range: b.display || (b.gt !== null ? `> ${b.gt}` : '≤ batas bawah'),
          desc:
            b.characteristic ||
            INDIVIDUAL_BANDS_DEFINITIONS.find(
              (d) => d.name.toLowerCase() === b.name.toLowerCase(),
            )?.desc ||
            '—',
        }))
      : INDIVIDUAL_BANDS_DEFINITIONS;

  return (
    <Card>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[16px] font-semibold text-ink">
          Definisi Kategori Kompetensi PELITA SLI
        </h3>
        <span className="text-xs text-ink-muted">Skala 0 – 100</span>
      </div>
      <div className="grid gap-2.5 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {list.map((def) => {
          const isCurrent = currentBand?.toLowerCase() === def.name.toLowerCase();
          return (
            <div
              key={def.name}
              className={cn(
                'rounded-lg p-3 text-xs border transition-all',
                isCurrent
                  ? 'border-primary bg-primary-soft/40 shadow-xs ring-1 ring-primary'
                  : 'border-border bg-subtle/50 text-ink-secondary',
              )}
            >
              <div className="flex items-center justify-between gap-1 mb-1">
                <span className={cn('font-bold text-[13px]', isCurrent ? 'text-primary' : 'text-ink')}>
                  {def.name}
                </span>
                <span className="font-semibold text-ink-muted tabular">{def.range}</span>
              </div>
              <p className="line-clamp-4 text-ink-secondary leading-relaxed">{def.desc}</p>
              {isCurrent && (
                <div className="mt-2 inline-flex items-center gap-1 rounded bg-primary/10 px-1.5 py-0.5 text-[11px] font-semibold text-primary">
                  ★ Posisi Saat Ini
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function IntensiveClassSummaryCard({
  intensiveClass,
}: {
  intensiveClass?: IndividualReport['intensiveClass'];
}) {
  if (!intensiveClass) return null;
  return (
    <Card className="border border-primary/20 bg-primary-soft/10">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-border pb-3">
        <div className="flex items-center gap-2">
          <span className="text-xl">🎓</span>
          <div>
            <h3 className="text-sm font-bold text-ink">Keterangan Keaktifan: Kelas Intensif PELITA SLI (10 Sesi Aktivitas)</h3>
            <p className="text-xs text-ink-secondary">Rekam jejak evaluasi pembelajaran & presensi pelatihan intensif.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider ${
              intensiveClass.status === 'lulus'
                ? 'bg-emerald-100 text-emerald-800'
                : intensiveClass.status === 'mengikuti'
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-amber-100 text-amber-800'
            }`}
          >
            {intensiveClass.status === 'lulus' ? 'Lulus Pelatihan' : intensiveClass.status}
          </span>
          {intensiveClass.certificateKey && (
            <a
              href={`/api/v1/files/${intensiveClass.certificateKey}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
            >
              📜 Unduh E-Sertifikat
            </a>
          )}
        </div>
      </div>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 text-center">
        <div className="rounded-lg bg-white p-3 border border-border">
          <span className="text-xs font-medium text-ink-secondary block">Tingkat Kehadiran</span>
          <span className="text-xl font-extrabold text-ink tabular block mt-0.5">{intensiveClass.attendancePct}%</span>
          <span className="text-[11px] text-ink-muted">10 Sesi Aktivitas</span>
        </div>
        <div className="rounded-lg bg-white p-3 border border-border">
          <span className="text-xs font-medium text-ink-secondary block">Evaluasi Pre & Post-Test</span>
          <span className="text-base font-bold text-ink tabular block mt-0.5">
            Pre: {intensiveClass.preTestScore ?? '—'} ➔ Post: <strong className="text-primary">{intensiveClass.postTestScore ?? '—'}</strong>
          </span>
          <span className="text-[11px] text-ink-muted">Skala Penilaian 0 – 100</span>
        </div>
        <div className="rounded-lg bg-white p-3 border border-border">
          <span className="text-xs font-medium text-ink-secondary block">Peningkatan (Gain Score)</span>
          <span
            className={`text-xl font-extrabold tabular block mt-0.5 ${
              (intensiveClass.gainScore ?? 0) > 0 ? 'text-emerald-600' : 'text-ink'
            }`}
          >
            {intensiveClass.gainScore !== null && intensiveClass.gainScore !== undefined
              ? (intensiveClass.gainScore > 0 ? `+${intensiveClass.gainScore}` : `${intensiveClass.gainScore}`)
              : '—'} Poin
          </span>
          <span className="text-[11px] text-ink-muted">Selisih Evaluasi Post - Pre</span>
        </div>
      </div>
      {/* Rincian Evaluasi Per Sesi jika ada */}
      {intensiveClass.sessionScores && intensiveClass.sessionScores.length > 0 && (
        <div className="mt-3 pt-3 border-t border-border/70">
          <span className="text-[11px] font-bold uppercase tracking-wider text-ink-secondary block mb-2">
            Rincian Evaluasi Nilai Per Sesi Kelas Intensif:
          </span>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
            {intensiveClass.sessionScores.map((sc) => (
              <div
                key={sc.sessionNumber}
                className="rounded-lg bg-white p-2 border border-border text-center shadow-2xs"
              >
                <span className="font-bold text-ink block text-[11px]">Sesi #{sc.sessionNumber}</span>
                <div className="text-[11px] tabular text-ink-muted mt-0.5">
                  {sc.preTest ?? '—'} ➔ <strong className="text-primary">{sc.postTest ?? '—'}</strong>
                </div>
                {sc.gain !== null && sc.gain !== undefined && (
                  <span
                    className={`block font-bold text-[10px] mt-0.5 ${
                      sc.gain > 0
                        ? 'text-emerald-600'
                        : sc.gain < 0
                          ? 'text-rose-600'
                          : 'text-gray-500'
                    }`}
                  >
                    ({sc.gain > 0 ? `+${sc.gain}` : sc.gain})
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {intensiveClass.notes && (
        <div className="mt-3 text-xs text-ink-secondary bg-white p-2.5 rounded border border-border">
          <strong>Catatan Fasilitator:</strong> {intensiveClass.notes}
        </div>
      )}
    </Card>
  );
}

function Body({ data }: { data: IndividualReport }) {
  const { individual, latest, dimensions, trend } = data;
  const [trendView, setTrendView] = useState<'dimensions' | 'overall'>('dimensions');

  if (!latest) return null;

  const points = trend
    .filter((t) => t.index !== null)
    .map((t) => ({ label: shortPeriod(t.periodName), value: t.index as number }));

  // Selisih dengan semester sebelumnya adalah angka yang paling ditunggu di
  // rapor individu — "naik atau tidak" lebih berarti daripada angka mutlaknya.
  const previous = points.length > 1 ? (points[points.length - 2]?.value ?? null) : null;
  const delta =
    previous !== null && latest.index !== null ? round1(latest.index - previous) : undefined;

  return (
    <div className="flex flex-col gap-5">
      <IndividualIdentityCard individual={individual} />
      <IntensiveClassSummaryCard intensiveClass={data.intensiveClass} />

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard
          label="Indeks kompetensi"
          value={latest.index === null ? '—' : round1(latest.index)}
          hint={latest.periodName}
          delta={delta}
        />
        <StatCard label="Kategori" value={latest.band ?? '—'} hint="hasil pembekuan saat kirim" />
        <StatCard
          label="Dimensi terlemah"
          value={weakest(dimensions)?.code ?? '—'}
          hint={weakest(dimensions)?.name}
          tone="warn"
        />
      </div>

      <BandDefinitionsCard currentBand={latest.band} bands={data.bands} />

      <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
        <Card>
          <h2 className="mb-3 text-[17px] font-semibold">Profil kompetensi</h2>
          <RadarChart
            items={dimensions.map((d) => ({ label: d.code, value: d.value, max: d.weight }))}
          />
        </Card>

        <Card>
          <h2 className="mb-3 text-[17px] font-semibold">Nilai per dimensi</h2>
          <BarList
            items={dimensions.map((d) => ({
              label: `${d.code} · ${d.name}`,
              value: d.weight > 0 ? (d.value / d.weight) * 100 : 0,
              hint: `· ${round1(d.value)} dari ${d.weight}`,
            }))}
            formatValue={(v) => `${Math.round(v)}%`}
          />
        </Card>
      </div>

      <Card>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-[17px] font-semibold text-ink">Tren Kompetensi Antar Semester</h2>
            <p className="text-[13px] text-ink-secondary">
              Perbandingan capaian setiap dimensi kompetensi melintasi semester evaluasi (Sumbu X: Dimensi, Sumbu Y: Capaian Skor/Persentase).
            </p>
          </div>
          {points.length > 1 && (
            <div className="flex rounded-[var(--radius-button)] border border-border bg-app p-0.5 text-xs font-medium">
              <button
                type="button"
                onClick={() => setTrendView('dimensions')}
                className={cn(
                  'rounded-[calc(var(--radius-button)-2px)] px-3 py-1.5 transition-colors cursor-pointer',
                  trendView === 'dimensions'
                    ? 'bg-white shadow-xs text-ink font-semibold'
                    : 'text-ink-secondary hover:text-ink',
                )}
              >
                Per Dimensi (Batang & Garis)
              </button>
              <button
                type="button"
                onClick={() => setTrendView('overall')}
                className={cn(
                  'rounded-[calc(var(--radius-button)-2px)] px-3 py-1.5 transition-colors cursor-pointer',
                  trendView === 'overall'
                    ? 'bg-white shadow-xs text-ink font-semibold'
                    : 'text-ink-secondary hover:text-ink',
                )}
              >
                Indeks Total
              </button>
            </div>
          )}
        </div>

        {trendView === 'dimensions' ? (
          <DimensionTrendChart trend={trend} />
        ) : (
          <TrendChart points={points} />
        )}
      </Card>
    </div>
  );
}

/** Dimensi dengan persentase capaian terendah — bukan nilai mentah terendah. */
function weakest(dims: IndividualReport['dimensions']) {
  const scored = dims.filter((d) => d.weight > 0);
  if (scored.length === 0) return null;
  return scored.reduce((a, b) => (a.value / a.weight <= b.value / b.weight ? a : b));
}
